<script>
import i18n from "@/plugins/i18n";
import { mapActions } from 'vuex';

export default {
    components: {
        CompanyDuplication: () => import("./companyDuplication.vue")
    },

    data() {
        return {
            companies: [],
            activeCompany: null
        }
    },

    computed: {
        mainHeaders() {
            return [
                {text: "#", width: "10%"},
                {text: i18n.t('additionalSettings.facilities.table.company_name'), width: "35%"},
                {text: i18n.t('additionalSettings.facilities.table.facility_active'), width: "35%"},
            ];
        },
        
        facilityHeaders() {
            return [
                {text: "#", width: "10%"},
                {text: i18n.t('additionalSettings.facilities.table.facility_name'), width: "20%"},
                {text: i18n.t('additionalSettings.facilities.table.facility_address'), width: "50%"},
                {text: i18n.t('additionalSettings.facilities.table.facility_phone'), width: "20%"}
            ];
        },

        emptyFacilityFields() {
            if (this.activeCompany && this.activeCompany.facilities) {
                const margin = this.activeCompany.facility_number - this.activeCompany.facilities.length;
                if (margin >= 1) {
                    let emptyFields = [];
                    for (let index = 0; index < margin; index++) {
                        emptyFields.push(
                            {
                                number: index + this.activeCompany.facility_number,
                                facility_name: "empty_facility_name",
                                facility_address: "empty_facility_address",
                                facility_phone: "empty_facility_phone"
                            }
                        )
                    }
                    return emptyFields;
                } else {
                    return [];
                }
            } else {
                return [];
            }
        }
    },

    mounted() {
        this._getCompaniesWithFacilityCounts();
    },

    methods: {
        ...mapActions("admin", [
            "getCompaniesWithFacilityCounts",
            "updateFacilitySlotsOfCompany"
        ]),

        ...mapActions("company", [
            "updateFacility",
            "createFacility",
            "deleteFacility"
        ]),

        _getCompaniesWithFacilityCounts() {
            this.getCompaniesWithFacilityCounts({})
                .then(res => {
                    this.companies = res.data;
                })
                .catch(({ response }) => {
                    this.$notify("error", response.data.message);
                })
        },

        _createFacility(facility) {
            const params = {
                facility_name: facility.facility_name,
                address: facility.facility_address,
                phone: facility.facility_phone,
                company_id: this.activeCompany.company_id,
                created_by: this.$user.user_id
            };

            this.createFacility(params)
                .then(res => {
                    facility["facility_id"] = res.data.facility_id;
                    this.activeCompany.facilities.push(facility);
                    this.$notify("success", res.data.messageName);
                })
                .catch(({ response }) => {
                    this._getCompaniesWithFacilityCounts();
                    this.$notify("error", response.data.messageName);
                })
        },

        _updateFacility(facility) {
            const params = {
                facility_id: facility.facility_id,
                body: {
                    facility_name: facility.facility_name,
                    address: facility.facility_address,
                    phone: facility.facility_phone,
                    updated_by: this.$user.user_id       
                }
            }

            this.updateFacility(params)
                .then(res => {
                    this.$notify("success", res.data.messageName);
                })
                .catch(({ response }) => {
                    this._getCompaniesWithFacilityCounts();
                    this.$notify("error", response.data.messageName);
                })
        },

        _deleteFacility(facility) {
            if (facility.facility_id) {
                const params = {
                    facility_id: facility.facility_id
                }

                this.deleteFacility(params)
                    .then(res => {
                        this.activeCompany.facilities = this.activeCompany.facilities.filter((_, index) => index != this.activeCompany.facilities.length -1);
                        this.$notify("success", res.data.messageName);
                    })
                    .catch(({ response }) => {
                        this._getCompaniesWithFacilityCounts();
                        this.$notify("error", response.data.messageName);
                    })
            } else {
                this._updateFacilitySlotsOfCompany(-1);
            }
        },

        _updateFacilitySlotsOfCompany(number) {
            if (this.activeCompany.facility_number + number < 1) return;

            const params = {
                company_id: this.activeCompany.company_id,
                facility_number: this.activeCompany.facility_number + number
            }

            if (number == -1 && this.emptyFacilityFields.length == 0) {
                this._deleteFacility(this.activeCompany.facilities[this.activeCompany.facilities.length-1]);
            }

            this.updateFacilitySlotsOfCompany(params)
                .then(res => {
                    this.activeCompany.facility_number = res.data.update_slots.facility_number;
                    this.$toast.open({
                        type: "success",
                        duration: 2000,
                        dismissable: true,
                        message: this.$parseResMessage(res.data.messageName)
                    });
                })
                .catch(({ response }) => {
                    this.$notify("error", response.error.message);
                })
        },

        handleClickInfo(item) {
            if (this.activeCompany != item) {
                this.activeCompany = item;
            } else {
                this.activeCompany = null;
            }
        },

        handleDuplicateCompany() {
            this.$refs.companyDuplication.openPopup();
        },

        handleDuplicationResult(result) {
            this.$refs.companyDuplication.startHideProcess();
            if (!result) {
                this.$notify('info', "Company duplication cancelled.", true);
            }
        }
    }
}
</script>

<template>
    <div class="companies-ctr">
        <div class="headers-row" style="border-bottom: thin solid black; font-weight: bold; text-align: left; pointer-events: none;">
            <div class="header-cell" v-for="header in mainHeaders" :key="header.text" :style="{width: header.width}">
                <div>
                    {{ header.text }}
                </div>
            </div>
        </div>
        <div class="companies-item" v-for="(item, index) in companies" :key="index">
            <div class="companies-info" :class="{'selected-info': activeCompany == item}"
                @click="handleClickInfo(item)"
            >
                <div class="companies-row-number" style="width: 10%;">
                    {{ index + 1 }}
                </div>
                
                <div class="companies-name" style="width: 35%;">
                    {{ item.company_name }}
                </div>

                <div class="facility-slots" style="width: 35%;" v-if="item.facility_number">
                    {{ item.facility_number }} slots ({{ item.facilities ? item.facilities.length : 0 }} active)
                </div>
                <div class="facility-slots" style="width: 35%;" v-else>
                    {{ $t('additionalSettings.facilities.misc.no_facilities') }}
                </div>
            </div>

            <div class="company-detail" v-if="activeCompany && activeCompany.company_id === item.company_id">
                <button v-if="item.facilities && item.facilities.length > 0"
                    class="sust-btn sust-btn-blue" style="margin-left: .2em; box-shadow: none;"
                    @click="handleDuplicateCompany()"
                >
                    {{ $t('additionalSettings.facilities.table.duplicate') }}
                </button>

                <div class="facility-headers-row">
                    <div class="facility-headers-cell" v-for="facilityHeader in facilityHeaders" :key="facilityHeader.text" :style="{'width': facilityHeader.width}">
                        {{ facilityHeader.text }}
                    </div>
                </div>
                <div class="facility-row" v-for="(facility, _index) in activeCompany.facilities" :key="_index">
                    <div class="facility-cell" :style="{'width': facilityHeaders[0].width}">
                        {{ _index + 1 }}
                        <i class="fa-regular fa-trash-can" @click="_deleteFacility(facility)"></i>
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[1].width}">
                        <input type="text" v-model="facility.facility_name" @change="_updateFacility(facility)">
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[2].width}">
                        <input type="text" v-model="facility.facility_address" @change="_updateFacility(facility)">
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[3].width}">
                        <input type="text" v-model="facility.facility_phone" @change="_updateFacility(facility)">
                    </div>
                </div>

                <div class="facility-row" v-for="(facility, _index) in emptyFacilityFields" :key="_index">
                    <div class="facility-cell" :style="{'width': facilityHeaders[0].width}" style="color: var(--invalid-input) !important;">
                        {{ facility.number }}
                        <i class="fa-regular fa-trash-can" @click="_deleteFacility(facility)"></i>
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[1].width}">
                        <input type="text" v-model="facility.facility_name" @change="_createFacility(facility)">
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[2].width}">
                        <input type="text" v-model="facility.facility_address" @change="_createFacility(facility)">
                    </div>
                    <div class="facility-cell" :style="{'width': facilityHeaders[3].width}">
                        <input type="text" v-model="facility.facility_phone" @change="_createFacility(facility)">
                    </div>
                </div>

                <div class="facility-slots">
                    <span>
                        {{ $t('additionalSettings.facilities.misc.slots') }}:&nbsp;
                    </span>
                    <i class="fa-solid fa-square-minus" @click="_updateFacilitySlotsOfCompany(-1)"></i>
                    <span>
                        {{ item.facility_number }}
                    </span>
                    <i class="fa-solid fa-square-plus" @click="_updateFacilitySlotsOfCompany(1)"></i>
                </div>
            </div>
        </div>

        <sust-popup ref="companyDuplication"
            size="xl"
            title="Company Duplication"
            :footer="false"
        >
            <company-duplication v-if="activeCompany" :companyId="activeCompany.company_id" @result="handleDuplicationResult($event)" />
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
    .companies-ctr {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        color: black;

        border-radius: 1em;
        overflow: hidden !important;
    }

    .companies-item {
        width: 100%;
        border-bottom: medium solid lightgray;

        &:last-of-type {
            border-bottom: 0;
        }
    }

    .companies-info {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        width: 100%;
        background-color: whitesmoke;

        > div {
            padding: .5em;
            margin-right: 1.5em;
        }

        transition: all 150ms;
        cursor: pointer;
        &:hover {
            background-color: var(--sustainfinity-faded-blue);
        }
    }

    .selected-info {
        background-color: var(--sustainfinity-blue);
        color: whitesmoke;
        

        &:hover {
            background-color: var(--sustainfinity-blue);
            color: whitesmoke;
            filter: brightness(90%);
        }
    }

    .headers-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        width: 100%;

        border-top-left-radius: 1em;
        border-top-right-radius: 1em;

        background-color: var(--sustainfinity-navy);
        color: whitesmoke;

        > div {
            padding: .5em;
            margin-right: 1.5em;
        }
    }

    .company-detail {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        width: 95%;

        justify-self: center;

        background-color: var(--sustainfinity-faded-blue);
        
        padding: 1em 0 1em 0;
    }

    .facility-headers-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%;
        border-bottom: thin solid black;
        font-weight: bolder;

        > div {
            padding: .5em .75em;
            margin-right: 1.5em;
        }
    }

    .facility-row {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%;

        border-bottom: thin solid gray;

        > .facility-cell:first-of-type {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;

            > i {
                padding-left: .5em;
                color: var(--invalid-input);

                transition: all 150ms;
                cursor: pointer;
                &:hover {
                    filter: brightness(90%);
                }
            }
        }
    }

    .facility-cell {
        padding: .5em .75em;
        margin-right: 1.5em;

        &:first-of-type {
            line-height: 2.25em;
        }

        > input {
            background: 0;
            border: 0;
            outline: thin solid rgb(202, 202, 202);
            padding: .5em;
            border-radius: .2em;

            &:focus {
                outline: thin solid gray !important;
                background: lightgray !important;
            }

            width: 100%;
        }
    }

    .facility-slots {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;

        font-size: 1.25em;

        padding: 1em .25em;


        > * {
            padding-right: .5em;
        }

        > i {
            &:first-of-type {
                color: var(--invalid-input);
            }
            &:last-of-type {
                color: var(--sustainfinity-green);
            }

            transform: translateY(5%);

            transition: all 150ms;
            cursor: pointer;
            &:hover {
                filter: brightness(90%);
            }
        }
    }
</style>